import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * A dashboard-only, atomic snapshot. A different account/table/date never sees
 * the previous key's figures. Background failures preserve a successful snapshot
 * for the SAME key, while obsolete requests cannot overwrite a newer result.
 */
export function useDashboardResource({ key, enabled = true, load, intervalMs = 30000 }) {
    const [snapshot, setSnapshot] = useState({ key: null, data: null, error: '', refreshing: false, updatedAt: null });
    const loader = useRef(load);
    const activeKey = useRef(key);
    const active = useRef(false);
    const generation = useRef(0);
    const request = useRef(null);
    loader.current = load;
    activeKey.current = key;

    const cancel = useCallback(() => {
        generation.current += 1;
        const previous = request.current;
        request.current = null;
        if (previous) {
            clearTimeout(previous.timeout);
            previous.controller.abort();
        }
    }, []);

    const refresh = useCallback(async () => {
        if (!enabled || !active.current || activeKey.current !== key) return;
        if (request.current?.key === key) return request.current.promise;
        cancel();
        const version = generation.current;
        const loadCurrent = loader.current;
        const controller = new AbortController();
        const current = () => active.current && activeKey.current === key && generation.current === version;
        setSnapshot(previous => ({
            ...(previous.key === key ? previous : { key, data: null, updatedAt: null }),
            error: '', refreshing: true,
        }));
        const pending = { key, controller, timeout: null, promise: null };
        request.current = pending;
        pending.promise = (async () => {
            let timedOut = false;
            let onAbort;
            try {
                // Race the timeout as well as aborting fetch: even a loader that
                // ignores cancellation must not leave the page loading forever.
                const timeout = new Promise((_, reject) => {
                    pending.timeout = setTimeout(() => {
                        timedOut = true;
                        controller.abort();
                        reject(new Error('The request timed out. Please retry.'));
                    }, 30000);
                });
                const cancelled = new Promise((_, reject) => {
                    onAbort = () => reject(new DOMException('Request cancelled', 'AbortError'));
                    controller.signal.addEventListener('abort', onAbort, { once: true });
                });
                const result = await Promise.race([Promise.resolve().then(() => loadCurrent({ signal: controller.signal })), timeout, cancelled]);
                if (!current() || controller.signal.aborted) return;
                setSnapshot({ key, data: result, error: '', refreshing: false, updatedAt: new Date() });
                return result;
            } catch (error) {
                if (!current() || (controller.signal.aborted && !timedOut)) return;
                setSnapshot(previous => ({
                    ...(previous.key === key ? previous : { key, data: null, updatedAt: null }),
                    refreshing: false,
                    error: timedOut ? 'The request timed out. Please retry.' : (error?.message || 'Could not refresh the latest data.'),
                }));
            } finally {
                clearTimeout(pending.timeout);
                controller.signal.removeEventListener('abort', onAbort);
                if (request.current === pending) request.current = null;
            }
        })();
        return pending.promise;
    }, [key, enabled, cancel]);

    const update = useCallback(next => {
        if (!active.current || activeKey.current !== key) return;
        // A completed local action takes precedence over any older GET snapshot.
        cancel();
        setSnapshot(previous => {
            if (previous.key !== key || previous.data === null) return previous;
            return { ...previous, data: typeof next === 'function' ? next(previous.data) : next,
                refreshing: false, error: '', updatedAt: new Date() };
        });
    }, [key, cancel]);

    useEffect(() => {
        active.current = true;
        if (enabled) refresh();
        return () => { active.current = false; cancel(); };
    }, [enabled, key, refresh, cancel]);

    useEffect(() => {
        if (!enabled) return undefined;
        const refreshWhenVisible = () => {
            if (document.visibilityState !== 'hidden' && navigator.onLine !== false) refresh();
        };
        const interval = setInterval(refreshWhenVisible, intervalMs);
        window.addEventListener('focus', refreshWhenVisible);
        window.addEventListener('online', refreshWhenVisible);
        document.addEventListener('visibilitychange', refreshWhenVisible);
        return () => {
            clearInterval(interval);
            window.removeEventListener('focus', refreshWhenVisible);
            window.removeEventListener('online', refreshWhenVisible);
            document.removeEventListener('visibilitychange', refreshWhenVisible);
        };
    }, [enabled, intervalMs, refresh]);

    const belongsToKey = snapshot.key === key;
    const data = enabled && belongsToKey ? snapshot.data : null;
    const error = enabled && belongsToKey ? snapshot.error : '';
    return { data, error, loading: enabled && data === null && !error,
        refreshing: enabled && belongsToKey && snapshot.refreshing,
        updatedAt: belongsToKey ? snapshot.updatedAt : null, refresh, update };
}
